/**
 * Color + karat picker for product-card-ref.
 * Reads combination map from JSON and updates the parent product-card image, price, and links.
 */
class ProductCardMetal extends HTMLElement {
  /** @type {{ color: string, karat: string }} */
  #selection = { color: '', karat: '' };

  /** @type {Record<string, { variantId: string, optionValue: string, colorKey: string, colorLabel: string, karat: string, imageUrl: string, price: string, available: boolean }>} */
  #combinations = {};

  connectedCallback() {
    const dataEl = this.querySelector('[data-metal-combinations]');
    if (!(dataEl instanceof HTMLScriptElement) || !dataEl.textContent) return;

    try {
      this.#combinations = JSON.parse(dataEl.textContent);
    } catch (error) {
      console.warn('[product-card-metal] Invalid combinations JSON', error);
      return;
    }

    this.#selection = {
      color: this.dataset.currentColor || '',
      karat: this.dataset.currentKarat || '',
    };

    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /**
   * @param {MouseEvent} event
   */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const colorBtn = target.closest('[data-metal-color]');
    const karatBtn = target.closest('[data-metal-karat]');

    if (colorBtn instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#selectColor(colorBtn.dataset.metalColor || '');
      return;
    }

    if (karatBtn instanceof HTMLElement) {
      event.preventDefault();
      event.stopPropagation();
      this.#selectKarat(karatBtn.dataset.metalKarat || '');
    }
  };

  /**
   * @param {string} colorKey
   */
  #selectColor(colorKey) {
    if (!colorKey) return;

    let combo = this.#combinations[`${colorKey}|${this.#selection.karat}`];
    if (!combo) {
      combo = Object.values(this.#combinations).find((entry) => entry.colorKey === colorKey);
    }
    if (!combo) return;

    this.#selection = { color: combo.colorKey, karat: combo.karat };
    this.#applyCombination(combo);
  }

  /**
   * @param {string} karat
   */
  #selectKarat(karat) {
    if (!karat) return;

    let combo = this.#combinations[`${this.#selection.color}|${karat}`];
    if (!combo) {
      combo = Object.values(this.#combinations).find((entry) => entry.karat === karat);
    }
    if (!combo) return;

    this.#selection = { color: combo.colorKey, karat: combo.karat };
    this.#applyCombination(combo);
  }

  /**
   * @param {{ variantId: string, colorKey: string, colorLabel: string, karat: string, imageUrl: string, price: string, available: boolean }} combo
   */
  #applyCombination(combo) {
    this.dataset.currentColor = combo.colorKey;
    this.dataset.currentKarat = combo.karat;

    this.querySelectorAll('[data-metal-color]').forEach((el) => {
      const active = el instanceof HTMLElement && el.dataset.metalColor === combo.colorKey;
      el.classList.toggle('product-card-metal__swatch--active', active);
      if (active) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });

    this.querySelectorAll('[data-metal-karat]').forEach((el) => {
      const active = el instanceof HTMLElement && el.dataset.metalKarat === combo.karat;
      el.classList.toggle('product-card-metal__karat--active', active);
      if (active) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
    });

    const label = this.querySelector('[data-metal-color-label]');
    if (label) label.textContent = combo.colorLabel;

    const card = this.closest('product-card');
    if (!(card instanceof HTMLElement)) return;

    if (combo.imageUrl) {
      const image = card.querySelector('.product-card-ref__image');
      if (image instanceof HTMLImageElement) {
        image.src = combo.imageUrl;
        image.srcset = '';
      }
    }

    if (combo.price) {
      const priceEl = card.querySelector('.product-card-ref__price');
      if (priceEl) priceEl.innerHTML = combo.price;
    }

    if (typeof card.applyVariantToLinks === 'function') {
      card.applyVariantToLinks(combo.variantId);
    } else {
      const link = card.querySelector('a.product-card__link');
      if (link instanceof HTMLAnchorElement && link.href) {
        const url = new URL(link.href);
        url.searchParams.set('variant', combo.variantId);
        link.href = url.toString();
      }
    }
  }
}

if (!customElements.get('product-card-metal')) {
  customElements.define('product-card-metal', ProductCardMetal);
}
